import { downloadPdf } from "@/lib/downloadPdf";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useRouter } from "@/lib/router-helpers";
import { getDay, getEthiopianMonth, toEthiopianTime } from "@/lib/ethiopian-date-conversion";
import { toEthiopian } from "ethiopian-calendar-new";

export default function TiketModal({ selected, closeModal }: { selected: any, closeModal: () => void }) {
  const [mounted, setMounted] = useState(false);
  const [qrLoaded, setQrLoaded] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();

  const dateObj = useMemo(() => (selected?.datetime ? new Date(selected.datetime) : new Date()), [selected?.date])
  const { year, month, day } = toEthiopian(dateObj.getFullYear(), dateObj.getMonth() + 1, dateObj.getDate());
  const amDate = `${t(getDay(dateObj.getDay(), "am"))}፣ ${getEthiopianMonth(month)} ${day}፣ ${year} `
  const enDate = dateObj.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "short", day: "2-digit" })
  const timeLabel = useMemo(() => {
    const dt = selected?.datetime;
    if (!dt) return ""
    const date = new Date(dt)
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const modifier = hours >= 12 ? "PM" : "AM"
    const time12h = `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')}`
    
    return toEthiopianTime(time12h, modifier)
  }, [selected?.datetime, selected?.date])


  useEffect(() => {
    setMounted(true);
    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!mounted) return null;
  
  


  return createPortal(
    <div className="fixed inset-0 bg-black/15 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={closeModal}
        className="absolute "
      />

      {/* Modal Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Logos */}
        <div className="pt-8 pb-4 flex items-center justify-center gap-4 border-b border-gray-100 mx-8">
            <div className="h-6 relative w-24">
                 <img src="/images/cheche.svg" alt="Cheche" className="absolute inset-0 w-full h-full object-contain" />
            </div>
            <div className="h-6 w-px bg-gray-300"></div>
            <div className="h-8 relative w-24">
                 <img src="/images/qetero_logo_text_3.svg" alt="Qetero" className="absolute inset-0 w-full h-full object-contain" />
            </div>
        </div>

        <div className="px-8 py-6 space-y-6">
            {/* Details List */}
            <div className="space-y-3 text-[15px]">
                <div className="flex gap-3">
                    <span className="font-bold text-gray-900 min-w-[60px]">{t("User")}:</span>
                    <span className="text-gray-600 font-medium">{selected.firstName} {selected.lastName}</span>
                </div>
                <div className="flex gap-3">
                    <span className="font-bold text-gray-900 min-w-[60px]">{t("branch")}:</span>
                    <span className="text-gray-600 font-medium">{t(selected.branch)}</span>
                </div>
                <div className="flex gap-3">
                    <span className="font-bold text-gray-900 min-w-[60px]">{t("service")}:</span>
                    <span className="text-gray-600 font-medium">{t(selected.category)}</span>
                </div>
                <div className="flex gap-3">
                    <span className="font-bold text-gray-900 min-w-[60px]">{t("time")}:</span>
                    <div className="flex flex-col">
                    <span className="text-gray-600 font-medium">{timeLabel}, {amDate} </span>
                    <span className="text-gray-600 font-medium">{timeLabel}, {enDate}</span>
                    </div>
                </div>
            </div>

            <div className="h-px bg-gray-100 w-full"></div>

            {/* QR Code */}
            <div className="flex flex-col items-center justify-center gap-4">
                <div className="p-1 bg-white relative min-h-[192px] min-w-[192px] flex items-center justify-center">
                    {!qrLoaded && (
                        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg" />
                    )}
                    <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(
                        selected.cnr ?? selected.id
                        )}`}
                        alt="qr"
                        className={`w-48 h-48 object-contain mix-blend-multiply transition-opacity duration-300 ${qrLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setQrLoaded(true)}
                    />
                </div>
                <p className="text-center text-gray-600 text-sm max-w-[280px] leading-relaxed font-medium">
                    {t("qrCodeInstruction")}
                </p>
            </div>
        </div>

        {/* Footer Buttons */}
        <div className="p-8 pt-2 flex flex-col gap-3">
            <div className="flex gap-4">
                <button
                className="flex-1 h-12 rounded-full border-2 border-pink-500 text-pink-600 text-sm font-bold hover:bg-pink-50 transition-colors"
                onClick={closeModal}
                >
                {t("close")}
                </button>
                <button
                className="flex-1 h-12 rounded-full bg-pink-600 text-white text-sm font-bold shadow-lg shadow-pink-200 hover:bg-pink-700 transition-all"
                onClick={() => downloadPdf({
                  ...selected,
                  appointmentTime: selected?.datetime,
                  userMobile: selected?.phone || selected?.mobile || selected?.userMobile,
                })}
                >
                {t("downloadPdf")}
                </button>
            </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}